import { Button } from "@/components/ui/baseUi/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { type SimpleFormik } from "@/typings/formik"
import { CalendarIcon } from "@images/box/calendarIcon"
import { type BoxSlotTemplateInputKey } from "@modules/boxSlotTemplate/boxSlotTemplateFormikType"
import { word } from "@modules/core/utils/i18n"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import { useCallback, useImperativeHandle, useState } from "react"
import { Text } from "@/components/ui/baseUi/text"
import type { BoxSlotTemplateFormikEntity } from "@/modules/boxSlotTemplate/entities/boxSlotTemplateFormikEntity"

export type BoxSlotTemplateFormModalRef = {
  open: () => void
  changedKeys: () => BoxSlotTemplateInputKey[]
}

export type BoxSlotTemplateFormModalProps = {
  formikInitialValues: BoxSlotTemplateFormikEntity
  formik: SimpleFormik<BoxSlotTemplateFormikEntity, BoxSlotTemplateInputKey>
  handleSubmit: () => Promise<void>
  ref: React.Ref<BoxSlotTemplateFormModalRef>
}

type tagProps = {
  title: string
  value: BoxSlotTemplateInputKey[]
  notification: boolean
}[]

export function BoxSlotTemplateFormModal({ formik, handleSubmit, ref }: BoxSlotTemplateFormModalProps) {
  const [showModal, setShowModal] = useState(false)
  const [isSelected, setIsSelected] = useState({
    now: false,
    future: false,
  })

  const tag: tagProps = [
    {
      title: word("box.slotTemplates.form.modal.tag.scheduleChange"),
      value: ["weekDay", "startDateTime", "endDateTime"],
      notification: true,
    },
    {
      title: word("box.slotTemplates.form.modal.tag.reservation"),
      value: ["limitEnd", "limitStart", "allowMultiBooking"],
      notification: false,
    },
    {
      title: word("box.slotTemplates.form.modal.tag.hostingCapacity"),
      value: ["participantLimit"],
      notification: false,
    },
    {
      title: word("box.slotTemplates.form.modal.tag.areaChange"),
      value: ["location"],
      notification: true,
    },
  ]

  const changedKeys = (): BoxSlotTemplateInputKey[] => {
    const changedKeys: BoxSlotTemplateInputKey[] = []
    for (const key in formik.initialValues) {
      if (
        formik.initialValues[key as keyof BoxSlotTemplateFormikEntity]?.toString() !==
        formik.values[key as keyof BoxSlotTemplateFormikEntity]?.toString()
      ) {
        changedKeys.push(key as BoxSlotTemplateInputKey)
      }
    }
    return changedKeys
  }

  const onHide = useCallback(() => {
    setShowModal(false)
  }, [])

  const onSubmit = async () => {
    await formik.setFieldValue("impactAlreadyCreatedSlot", isSelected.now)
    await handleSubmit()
    onHide()
  }

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
    changedKeys: () => changedKeys(),
  }))

  const dateForTwoWeek = new Date()
  dateForTwoWeek.setDate(dateForTwoWeek.getDate() + 15)

  return (
    <Dialog open={showModal} onOpenChange={onHide}>
      <DialogContent className="mt-3 flex w-[512px] flex-col rounded-4xl">
        {/* HEADER */}
        <DialogHeader className="flex flex-col flex-wrap content-center justify-center gap-2 pt-5 text-center">
          <DialogTitle>
            <div className="flex w-full content-center justify-center">
              <CalendarIcon color="currentColor" />
            </div>
            <Text fontStyle="TL" className="text-textDefault-heading">
              {word("box.slotTemplates.form.modal.header.title")}
            </Text>
          </DialogTitle>

          <div className="flex flex-col items-center gap-2">
            <div className="flex flex-wrap content-center justify-center gap-2">
              {tag
                .filter((t) => t.value.some((v) => changedKeys().includes(v)))
                .map((t, index) => (
                  <Text
                    key={index}
                    fontStyle="LL"
                    className="rounded-full bg-neutral-100 px-3 py-2 text-textDefault-body"
                  >
                    {t.title}
                  </Text>
                ))}
            </div>
            <Text fontStyle="BS" className="text-textDefault-caption">
              {word("box.slotTemplates.form.modal.header.subtitle")}
            </Text>
          </div>
        </DialogHeader>

        {/* CONTENT */}
        <Text fontStyle="BM" className="mt-5 flex text-center text-textDefault-caption">
          {word("box.slotTemplates.form.modal.content.title")}
        </Text>
        <div className="mt-3 flex gap-3">
          <div
            className={`flex w-6/12 cursor-pointer flex-col gap-3 rounded-2xl border p-3 ${isSelected.now ? "border-primary-400" : "cursor-pointer border-neutral-700"}`}
            onClick={() => setIsSelected({ now: true, future: false })}
          >
            <Text fontStyle="TS" className="text-center text-textDefault-body">
              {word("box.slotTemplates.form.modal.content.now")}
            </Text>
            {tag.some((t) => t.notification && t.value.some((v) => changedKeys().includes(v))) && (
              <div className="flex flex-col gap-2">
                <Text fontStyle="BS" className="text-center text-textDefault-caption">
                  {word("box.slotTemplates.form.modal.content.athele")}
                </Text>
                <Text fontStyle="BS" className="text-center text-textDefault-caption">
                  {word("box.slotTemplates.form.modal.content.notification")}
                </Text>
                <Text fontStyle="BS" className="text-center text-textDefault-caption">
                  {word("box.slotTemplates.form.modal.content.email")}
                </Text>
              </div>
            )}
          </div>
          <div
            className={`flex w-6/12 cursor-pointer flex-col gap-3 rounded-2xl border p-3 ${isSelected.future ? "border-primary-400" : "cursor-pointer border-neutral-700"}`}
            onClick={() => setIsSelected({ now: false, future: true })}
          >
            <Text fontStyle="TS" className="text-center text-textDefault-body">
              {word("box.slotTemplates.form.modal.content.future")}
            </Text>
            <Text fontStyle="BS" className="text-center text-textDefault-caption">
              {` ${word("box.slotTemplates.form.modal.content.starting")} ${formatDateWithHyphen(dayjs(dateForTwoWeek).format("L"))}`}
            </Text>
          </div>
        </div>
        {/* BUTTON */}
        <div className="mt-5 flex flex-row content-center justify-center gap-2">
          <Button size="lg" variant="outline" onClick={() => onHide()} className="flex-1">
            {word("global.cancel")}
          </Button>
          <Button
            size="lg"
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={(!isSelected.future && !isSelected.now) || formik.isSubmitting}
            className="flex-1"
          >
            {word("global.check")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
